import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useUseCases } from 'UseCaseProvider';

import { Dropdown } from '../../../../components/Dropdown';
import { StyledDropdownOption } from '../../../../components/Dropdown/styles';
import type { Contact } from '../../../../domain/contact';
import type { CrmAccount } from '../../../../domain/crm';
import { Crm } from '../../../../domain/crm';
import { useCrmStatusQuery } from '../../../../features/crm/useCrmStatusQuery';
import { translate } from '../../../../services/i18n';
import { CrmExportPreviewModal } from '../CrmExportPreviewModal';

import { StyledCrmExportButton } from './styles';
import { useContactExportTracking } from './useContactExportTracking';

export const NEW_ACCOUNT_VALUE = 'new_account';

export type CrmExportContainerProps = {
  crm: Crm;
  contact: Contact;
};

export const CrmExportContainer = ({ crm, contact }: CrmExportContainerProps) => {
  const { exportContactsToCrm } = useUseCases();
  const crmStatusQuery = useCrmStatusQuery(crm);
  const { handleTrackExportEvent } = useContactExportTracking(crm);

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [isPreviewModalOpened, setIsPreviewModalOpened] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<CrmAccount | null>(null);

  const isLeadExportEnabled = crmStatusQuery.data?.isLeadExportEnabled;
  const isContactExportEnabled = crmStatusQuery.data?.isContactExportEnabled;
  const isDropdownShown = crm === Crm.salesforce && isLeadExportEnabled && isContactExportEnabled;

  const { mutate, isPending } = useMutation({
    mutationFn: exportContactsToCrm,
  });

  const exportAsLead = () => {
    handleTrackExportEvent('export contact', `export as lead`);
    mutate({
      payload: {
        countries: [999],
        leads: [
          {
            contactId: contact.id,
            domain: contact.site,
          },
        ],
      },
      crm,
      onSuccess: () => handleTrackExportEvent('export success', 'export as lead'),
      onError: () => handleTrackExportEvent('export failed', 'export as lead'),
    });
  };

  const exportAsContact = (selectedAccountProp?: CrmAccount) => {
    const selectedAccountValue = selectedAccountProp || selectedAccount;
    const payload = {
      countries: [999],
      contacts: [
        {
          contactId: contact.id,
          domain: contact.site,
          accountId: selectedAccountValue?.id === NEW_ACCOUNT_VALUE ? undefined : selectedAccountValue?.id,
        },
      ],
    };

    mutate({
      payload,
      crm,
      onSuccess: () => handleTrackExportEvent('export success', 'export as contact'),
      onError: () => handleTrackExportEvent('export failed', 'export as contact'),
    });
  };

  const handleClickCrmExportButton = () => {
    if (isDropdownShown) {
      setIsDropdownOpened(true);
      return;
    }

    if (crm === Crm.hubspot || (crm === Crm.salesforce && isContactExportEnabled)) {
      setIsPreviewModalOpened(true);
      return;
    }

    if (isLeadExportEnabled) {
      exportAsLead();
    }
  };

  const handleExportAsLeadClick = () => {
    setIsDropdownOpened(false);
    exportAsLead();
  };

  const handleExportAsContactClick = () => {
    setIsPreviewModalOpened(true);
    setIsDropdownOpened(false);
  };

  const handleConfirmPreviewClick = (selectedAccount: CrmAccount) => {
    handleTrackExportEvent('export contact');
    exportAsContact(selectedAccount);
    setIsPreviewModalOpened(false);
    setIsDropdownOpened(false);
  };

  const handleClosePreviewModal = () => {
    handleTrackExportEvent('cancel export');
    setIsPreviewModalOpened(false);
  };

  return (
    <>
      <StyledCrmExportButton
        onClick={handleClickCrmExportButton}
        isLoading={isPending}
        isActive={isDropdownOpened}
        data-automation="CrmExportButton"
      >
        {!isPending ? <SWReactIcons iconName={crm} size="xs" iconColor={colorsPalettes.carbon[500]} /> : null}
        {translate('contact_card.export_crm')}
      </StyledCrmExportButton>
      {isDropdownShown ? (
        <Dropdown
          isOpened={isDropdownOpened}
          onClose={() => setIsDropdownOpened(false)}
          placement="right"
          minWidth={120}
        >
          <StyledDropdownOption onClick={handleExportAsContactClick} data-automation="CrmExportDd">
            {translate('contact_card.export_sf.as_contact')}
          </StyledDropdownOption>
          <StyledDropdownOption onClick={handleExportAsLeadClick} data-automation="CrmExportDdOption">
            {translate('contact_card.export_sf.as_lead')}
          </StyledDropdownOption>
        </Dropdown>
      ) : null}
      <CrmExportPreviewModal
        isOpen={isPreviewModalOpened}
        onClose={handleClosePreviewModal}
        crm={crm}
        contact={contact}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        onConfirm={handleConfirmPreviewClick}
      />
    </>
  );
};
